// Lịch Luyện closed-form (ENEMIES §2, BALANCE §5.1) + Trữ Lượng vùng tài nguyên (WORLD §3.2).
//  - Không mô phỏng trận: số lần hạ = killsPerMin × thời lượng × hiệu suất, tích phần lẻ theo vùng.
//  - Lần hạ thứ k của vùng xảy ra ở mốc (k − phần lẻ đầu quãng) / tốc độ: cùng một quá trình liên tục
//    dù chia quãng thế nào (online từng giây, offline khối 8h). Trữ Lượng xét theo mốc của từng lần hạ.
//  - Mỗi lần hạ roll bằng RNG riêng seed = hash(seed, 'trial', vùng, k), tách luồng con theo loại roll
//    (loài, nguyên liệu, đồ, Bụi): một roll trúng / trượt không đẩy lệch các roll khác → cùng chuỗi
//    kết quả, cùng phân phối, tất định.
//  - Thu thập theo giờ: tích phần lẻ, không RNG. Trữ Lượng < RESERVE_LOW thì sản lượng ×0,5.
//  - Mật độ, linh thạch, tu vi phụ theo quái (L quái, màu cấp BALANCE §1.4), không theo người chơi.
//  - Idle không bao giờ chết, không ép trận (Bất biến 4): hàm không có nhánh thua.
import { BALANCE } from '@data/balance';
import { fromStageL, giai as giaiOf, powerIndex } from '@data/powerScale';
import { familyDef, templateDef, zoneDef, TRIAL_ZONES, type TrialZoneDef } from '@data/content/zones';
import { isWalletCurrency } from '@data/currencies';
import {
  EQUIP_SLOTS,
  type ItemInstance,
  type SlotKind,
  type TriKey,
  type WeaponType,
} from '@data/items/types';
import { createRng, deriveSeed, type Rng } from '../core/rng';
import { HOUR_MS, WATCH_HOURS } from '../core/time';
import { pickWeighted, rollIdleRarity, rollItem } from '../items/generate';
import { slotKindOf, type UtilityBlock } from '../items/stats';

export interface ZoneRuntime {
  /** Trữ Lượng 0..100 ở mốc `atMs`. */
  reserve: number;
  atMs: number;
  /**
   * Phần lẻ tích lũy của vùng: sản vật thu thập theo giờ (theo id), linh thạch rơi (`killStone`),
   * số lần hạ (`kill`). Thiếu khóa thì coi là 0.
   */
  gatherFrac: Record<string, number>;
}

export interface TrialState {
  zones: Record<string, ZoneRuntime>;
  /** Tổng số lần hạ đã roll (chỉ số RNG của lần hạ kế tiếp), theo vùng. */
  kills: Record<string, number>;
  /**
   * Cũ (save v4): phần lẻ số lần hạ dùng chung mọi vùng. Không còn đọc; phần lẻ nay nằm ở
   * `zones[vùng].gatherFrac.kill`. Giữ trường để save cũ / lớp state không đổi hình; luôn ghi 0.
   */
  killFrac: number;
}

/** Khóa phần lẻ số lần hạ trong `ZoneRuntime.gatherFrac`. */
export const KILL_FRAC_KEY = 'kill';
const KILL_STONE_KEY = 'killStone';

export function defaultTrial(nowMs: number): TrialState {
  return {
    zones: Object.fromEntries(TRIAL_ZONES.map((z) => [z.id, { reserve: 100, atMs: nowMs, gatherFrac: {} }])),
    kills: {},
    killFrac: 0,
  };
}

export interface TrialCtx {
  /** Bậc L người chơi (quái theo L, kẹp trong khoảng vùng). */
  playerL: number;
  /** Chỉ số cảnh giới gốc 0 của người chơi (tham khảo; linh thạch / mật độ tính theo cảnh giới quái). */
  realmIdx: number;
  /** ATK người chơi (tốc độ hạ). */
  atk: number;
  utility: UtilityBlock;
  /** Hiệu suất (online 1; offline 0,60…0,85). */
  eff: number;
  /** Loại vũ khí đang dùng (loot cá nhân). */
  weaponPref?: WeaponType;
  /** Tam Nguyên trội của vũ khí đang dùng (loot cá nhân, ITEMS §7.5). */
  favorTri?: TriKey;
  /** Ô được ưu tiên ×1,5 (ô trống / yếu nhất, ITEMS §7.5). */
  focusSlots?: readonly SlotKind[];
}

export interface TrialLoot {
  kills: number;
  qi: number;
  items: ItemInstance[];
  /** Tiền tệ ví (linh thạch, Bụi, Toái Linh, Luyện Khí Thạch). */
  currency: Record<string, number>;
  /** Vật phẩm xếp chồng (nguyên liệu). */
  goods: Record<string, number>;
}

export function emptyLoot(): TrialLoot {
  return { kills: 0, qi: 0, items: [], currency: {}, goods: {} };
}

function add(rec: Record<string, number>, id: string, n: number): void {
  if (n > 0) rec[id] = (rec[id] ?? 0) + n;
}

export function addLoot(into: TrialLoot, l: TrialLoot): void {
  into.kills += l.kills;
  into.qi += l.qi;
  into.items.push(...l.items);
  for (const [k, v] of Object.entries(l.currency)) add(into.currency, k, v);
  for (const [k, v] of Object.entries(l.goods)) add(into.goods, k, v);
}

// ── Trữ Lượng
const clampR = (r: number): number => Math.max(0, Math.min(100, r));

/** Trữ Lượng sau `hours` với tốc độ `v` điểm/giờ, và số giờ "hiệu dụng" (dưới ngưỡng tính ×0,5). */
export function reserveSegment(r0: number, v: number, hours: number): { r1: number; effHours: number } {
  const low = BALANCE.RESERVE_LOW;
  let high: number;
  if (v < 0) high = r0 >= low ? Math.min(hours, (r0 - low) / -v) : 0;
  else if (r0 >= low) high = hours;
  else high = v > 0 ? Math.max(0, hours - (low - r0) / v) : 0;
  return { r1: clampR(r0 + v * hours), effHours: high + BALANCE.RESERVE_LOW_MULT * (hours - high) };
}

export function regenPerHour(z: TrialZoneDef): number {
  return z.regenPerWatch / WATCH_HOURS;
}

/** Trữ Lượng hiện tại của một vùng ở `atMs` nếu từ mốc lưu tới giờ vùng chỉ hồi (không bị khai thác). */
export function reserveAt(rt: ZoneRuntime, z: TrialZoneDef, atMs: number): number {
  const h = Math.max(0, atMs - rt.atMs) / HOUR_MS;
  return clampR(rt.reserve + regenPerHour(z) * h);
}

// ── Màu cấp (BALANCE §1.4)
export type LevelColor = keyof typeof BALANCE.LEVEL_COLOR_REWARD;

/** Màu của quái bậc `monsterL` với người chơi bậc `playerL`: theo ΔL, hoặc đầu lâu khi Δlog ≥ 1. */
export function levelColor(monsterL: number, playerL: number): LevelColor {
  const m = fromStageL(monsterL);
  const p = fromStageL(playerL);
  if (Math.log10(powerIndex(m.realm, m.s)) - Math.log10(powerIndex(p.realm, p.s)) >= 1) return 'skull';
  const dl = monsterL - playerL;
  const b = BALANCE.LEVEL_COLOR_MAX_DL;
  if (dl <= b.grey) return 'grey';
  if (dl <= b.green) return 'green';
  if (dl <= b.yellow) return 'yellow';
  if (dl <= b.orange) return 'orange';
  if (dl <= b.red) return 'red';
  return 'skull';
}

// ── Tốc độ hạ (BALANCE §5.1)
export function monsterL(z: TrialZoneDef, playerL: number): number {
  return Math.max(z.lMin, Math.min(z.lMax, playerL));
}

/** Chỉ số cảnh giới gốc 0 của quái trong vùng (mật độ mặc định, linh thạch 4^r). */
function monsterRealmIdx(l: number): number {
  return fromStageL(l).realm - 1;
}

export function killsPerMin(z: TrialZoneDef, ctx: TrialCtx): number {
  if (z.monsters.length === 0) return 0;
  const l = monsterL(z, ctx.playerL);
  const { realm, s } = fromStageL(l);
  const hp = BALANCE.HP_STD * powerIndex(realm, s);
  const m = BALANCE.DPS_MODEL;
  const dps =
    m.hitsPerS *
    m.avgCoef *
    m.crit *
    ctx.atk *
    (100 / (100 + BALANCE.PLAYER_DEF_BASE)) *
    m.partyMult *
    BALANCE.AUTO_EFF;
  const ttk = hp / Math.max(1e-9, dps);
  const table = BALANCE.ZONE_DENSITY_BY_REALM;
  const density = z.density ?? table[Math.min(monsterRealmIdx(l), table.length - 1)]!;
  return Math.min(density, 60 / (ttk + BALANCE.TRAVEL_S));
}

/** R1 tu vi phụ mỗi lần hạ (kỳ vọng, không rút RNG): 0,6 × P quái × zone.qiMul × thưởng màu. */
export function sideQiPerKill(z: TrialZoneDef, playerL: number): number {
  if (z.monsters.length === 0) return 0;
  const l = monsterL(z, playerL);
  const { realm, s } = fromStageL(l);
  return (
    BALANCE.SIDE_QI_PER_P *
    powerIndex(realm, s) *
    BALANCE.ZONE_QI_MUL_DEFAULT *
    BALANCE.ROLL.qi *
    BALANCE.LEVEL_COLOR_REWARD[levelColor(l, playerL)]
  );
}

/** Trọng số ô rơi = số ô trang bị của loại đó (nhẫn 2), ô ưu tiên ×LOOT_SLOT_FOCUS_MULT. */
const DROP_SLOTS: readonly { kind: SlotKind; n: number }[] = [...new Set(EQUIP_SLOTS.map(slotKindOf))].map(
  (kind) => ({ kind, n: EQUIP_SLOTS.filter((s) => slotKindOf(s) === kind).length }),
);

// ── Một lần hạ: 4 roll (ENEMIES §2.2), mỗi roll một luồng RNG con
function rollKill(
  z: TrialZoneDef,
  ctx: TrialCtx,
  rng: Rng,
  uid: string,
  atMs: number,
  reserveMult: number,
  out: TrialLoot,
): void {
  const tpl = templateDef(pickWeighted(rng.fork('monster'), z.monsters, (m) => m.weight).template);
  const fam = familyDef(tpl.family);
  const { realm, s } = fromStageL(monsterL(z, ctx.playerL));
  const u = ctx.utility;
  // R2 nguyên liệu họ quái (Thu Hoạch %, Trữ Lượng thấp ×0,5)
  const rm = rng.fork('material');
  const pMat = Math.min(1, BALANCE.ROLL.material * (1 + u.idleLoot / 100) * reserveMult);
  if (rm.next() < pMat) {
    const elite = rm.next() < BALANCE.ROLL_MATERIAL_TIER_B;
    const list = elite ? fam.materials.elite : fam.materials.common;
    add(out.goods, list[rm.int(list.length)]!, 1);
  }
  // R3 đồ nền
  const re = rng.fork('equip');
  if (re.next() < BALANCE.ROLL.equip) {
    const rarity = rollIdleRarity(re, u.itemFind);
    const slot = pickWeighted(
      re,
      DROP_SLOTS,
      (x) => x.n * (ctx.focusSlots?.includes(x.kind) ? BALANCE.LOOT_SLOT_FOCUS_MULT : 1),
    ).kind;
    out.items.push(
      rollItem(
        {
          uid,
          slot,
          rarity,
          giai: giaiOf(realm, s),
          source: 'common',
          weaponPref: ctx.weaponPref,
          favorTri: ctx.favorTri,
          origin: z.id,
          atMs,
        },
        re,
      ),
    );
  }
  // R4 Bụi / Toái Linh
  const rd = rng.fork('dust');
  if (rd.next() < BALANCE.ROLL.dust) {
    if (rd.next() < BALANCE.ROLL_DUST_SHARD_SHARE) add(out.currency, `spiritShard.${fam.element}`, 1);
    else {
      const [lo, hi] = BALANCE.ROLL_DUST_AMOUNT;
      add(out.currency, 'dust', lo + rd.int(hi - lo + 1));
    }
  }
}

/**
 * Lịch Luyện ở `zoneId` trong [fromMs, toMs] với ngữ cảnh người chơi cố định (tầng trên cắt quãng ở
 * mốc đột phá tiểu cảnh, `src/sim/world/settle.ts`). Mọi vùng khác chỉ hồi Trữ Lượng (tính lười ở
 * lần đọc kế, `reserveAt`).
 */
export function settleTrial(
  input: TrialState,
  zoneId: string,
  fromMs: number,
  toMs: number,
  seed: number,
  ctx: TrialCtx,
): { state: TrialState; loot: TrialLoot } {
  const z = zoneDef(zoneId);
  const loot = emptyLoot();
  const spanMs = Math.max(0, toMs - fromMs);
  const hours = spanMs / HOUR_MS;
  const rt0 = input.zones[zoneId] ?? { reserve: 100, atMs: fromMs, gatherFrac: {} };
  const r0 = reserveAt(rt0, z, fromMs);
  const v = regenPerHour(z) - BALANCE.RESERVE_DRAIN_PER_H;
  const seg = reserveSegment(r0, v, hours);

  // Thu thập theo giờ (tích phần lẻ; linh thạch vụn nhân Linh Thạch Thu %).
  const frac = { ...rt0.gatherFrac };
  for (const g of z.gather) {
    const k =
      (g.id === 'spiritStone' ? 1 + ctx.utility.stoneFind / 100 : 1 + ctx.utility.idleLoot / 100) * ctx.eff;
    const total = (frac[g.id] ?? 0) + g.perHour * seg.effHours * k;
    const whole = Math.floor(total + 1e-9);
    frac[g.id] = total - whole;
    if (whole > 0) add(isWalletCurrency(g.id) ? loot.currency : loot.goods, g.id, whole);
  }

  // Quái thường
  let k = input.kills[zoneId] ?? 0;
  if (z.monsters.length) {
    const f0 = frac[KILL_FRAC_KEY] ?? 0;
    const perMs = (killsPerMin(z, ctx) * ctx.eff) / 60_000;
    const total = f0 + perMs * spanMs;
    const n = Math.floor(total + 1e-9);
    frac[KILL_FRAC_KEY] = Math.max(0, total - n);
    for (let j = 1; j <= n; j++, k++) {
      // Mốc của lần hạ thứ j trong quãng: phần lẻ f0 đã "đi" trước từ quãng trước.
      const at = Math.min(toMs, fromMs + (j - f0) / perMs);
      const rAt = clampR(r0 + (v * (at - fromMs)) / HOUR_MS);
      rollKill(
        z,
        ctx,
        createRng(deriveSeed(seed, 'trial', zoneId, k)),
        `k${(seed >>> 0).toString(36)}.${zoneId.slice(4)}.${k}`,
        Math.round(at),
        rAt >= BALANCE.RESERVE_LOW ? 1 : BALANCE.RESERVE_LOW_MULT,
        loot,
      );
    }
    loot.kills = n;
    loot.qi = n * sideQiPerKill(z, ctx.playerL);
    // Linh thạch rơi kèm mỗi lần hạ (không phải roll, cả màu xám), theo cảnh giới quái.
    const mr = monsterRealmIdx(monsterL(z, ctx.playerL));
    const st =
      (frac[KILL_STONE_KEY] ?? 0) +
      n * BALANCE.STONE_PER_KILL * Math.pow(4, mr) * (1 + ctx.utility.stoneFind / 100);
    const whole = Math.floor(st + 1e-9);
    frac[KILL_STONE_KEY] = st - whole;
    add(loot.currency, 'spiritStone', whole);
  }
  return {
    state: {
      zones: { ...input.zones, [zoneId]: { reserve: seg.r1, atMs: toMs, gatherFrac: frac } },
      kills: { ...input.kills, [zoneId]: k },
      killFrac: 0,
    },
    loot,
  };
}
